import { getNewSheetModeWorksheetName, NEW_SHEET_MODE_DATE_TIME_FORMAT } from '@shared/excel/excel';
import { RangeAddress } from '@shared/excel/range-address';
import { PULL_FLOWSTEP_ADDITIONAL_HEADERS } from '@shared/excel/runtime/flowstepExecution';
import { AddressResolverFactory } from '@shared/execution/address-resolver-factory';
import {
  ExecutorActionError,
  ExecutorActionEvent,
  ExecutorActionFinished,
  ExecutorActionInfo,
  ExecutorActionStarted,
  ExecutorCancelled,
  ExecutorNewWorksheetCreated,
} from '@shared/execution/executors/events';
import { AddressResolver, CellFormatter, ValueConverter } from '@shared/execution/types';
import { InsertBehaviour, WorkbookApi, WorkbookApiFactory } from '@shared/execution/workbook-api';
import { EventBus } from '@shared/infrastructure/app-event-bus';
import { ProviderClient, ProviderFactory } from '@shared/providers/provider';
import { isMetadata, MetadataResult, STREAM_METADATA } from '@shared/providers/provider-transport';
import { DataField } from '@shared/providers/types';
import { Flowstep } from '@shared/types/flowstep';
import { CancelError, CancelToken } from '@shared/utils/cancelToken';
import { NoDataError } from '@shared/utils/flowstep';
import dayjs from 'dayjs';
import { CellAddressPickerValue } from 'frontend/excel365-addin/src/v2/components/base/CellAddressPicker/types';
import Highland from 'highland';
import { PullMode } from 'shared/types/flowstep';
import Executor from '.';
void Highland;

interface InjectedDependencies {
  providerFactory: ProviderFactory;
  valueConverter: ValueConverter;
  workbookApiFactory: WorkbookApiFactory;
  eventBus: EventBus;
  addressResolverFactory: AddressResolverFactory;
  cellFormatter: CellFormatter;
}

class PullExecutor extends Executor {
  private providerFactory: ProviderFactory;
  private valueConverter: ValueConverter;
  private workbookApiFactory: WorkbookApiFactory;
  private eventBus: EventBus;
  private addressResolverFactory: AddressResolverFactory;
  private cellFormatter: CellFormatter;

  constructor(deps: InjectedDependencies) {
    super();
    this.providerFactory = deps.providerFactory;
    this.valueConverter = deps.valueConverter;
    this.workbookApiFactory = deps.workbookApiFactory;
    this.eventBus = deps.eventBus;
    this.addressResolverFactory = deps.addressResolverFactory;
    this.cellFormatter = deps.cellFormatter;
  }

  private timestamp: string | null = null;
  private recivedRowsCount = 0;
  flowstep!: Flowstep;
  worksheet: any;
  private workbookApi!: WorkbookApi<any>;
  private addressResolver!: AddressResolver;
  private provider!: ProviderClient;
  private timezone!: string;
  private signal?: AbortSignal;

  init(flowstep: Flowstep, context: any): void {
    this.flowstep = flowstep;
    this.timezone = context.timezone;
    this.signal = context.signal;

    this.workbookApi = this.workbookApiFactory(context);
    this.addressResolver = this.addressResolverFactory.getAddressResolver(flowstep, this.workbookApi);

    this.provider = this.providerFactory.getProviderClient(flowstep.provider, context);
  }

  async execute(cancelToken?: CancelToken) {
    try {
      this.eventBus.notify(new ExecutorActionStarted());
      this.flowstep = await this.addressResolver.replaceAddressesByValues(this.flowstep);

      const {
        pullMode,
        worksheetId,
        target: topLeftCellAddress,
        createDataTable,
        withDateTimeColumn,
        useDropdownsForPicklists,
      } = this.flowstep.options;

      const { objectProps, objectType } = this.flowstep.providerOptions;

      let targetWorksheetId = worksheetId;

      if (pullMode === PullMode.NEW_SHEET) {
        const dateTime = dayjs().format(NEW_SHEET_MODE_DATE_TIME_FORMAT);
        const worksheetName = getNewSheetModeWorksheetName(this.flowstep.name, dateTime);
        this.eventBus.notify(new ExecutorActionInfo(`Creating new worksheet ${worksheetName}`));
        targetWorksheetId = await this.workbookApi.createNewWorksheet(worksheetName);
        this.eventBus.notify(new ExecutorNewWorksheetCreated(worksheetName));
      }

      const fields = await this.provider.loadObjectFields(objectType);
      const usedFields = objectProps.map((propName: string) => fields.find((field) => field.name === propName));

      this.eventBus.notify(new ExecutorActionEvent('Detecting the target range'));

      this.worksheet = await this.workbookApi.getWorksheet?.(targetWorksheetId);
      const detectedRange = await this.workbookApi.detectRangeByCell(
        targetWorksheetId,
        topLeftCellAddress,
        usedFields.length,
      );

      const detectedHeaders = detectedRange ? await this.workbookApi.getColumnHeaders(detectedRange) : null;
      const isDateTimeColumnEnabled = pullMode === PullMode.APPEND && withDateTimeColumn;
      const needToAddDateTimeColumn =
        isDateTimeColumnEnabled && !detectedHeaders?.includes(PULL_FLOWSTEP_ADDITIONAL_HEADERS.AppendDateTime);

      if (isDateTimeColumnEnabled) {
        usedFields.push({
          name: PULL_FLOWSTEP_ADDITIONAL_HEADERS.AppendDateTime,
          label: PULL_FLOWSTEP_ADDITIONAL_HEADERS.AppendDateTime,
          type: 'datetime',
        } as DataField);
      }

      const extraColumnsCount = detectedHeaders ? usedFields.length - detectedHeaders.length : 0;
      const extendedRange =
        extraColumnsCount > 0 ? await this.workbookApi.insertColumns(detectedRange, extraColumnsCount) : null;

      const targetRange =
        extendedRange ||
        detectedRange ||
        (await this.workbookApi.getRangeBySize(targetWorksheetId, topLeftCellAddress, 1, usedFields.length));

      this.eventBus.notify(new ExecutorActionEvent('Receiving data'));

      const providerStream = await this.provider.getFlowstepData(this.flowstep, cancelToken);
      const rowNumberFormats = this.cellFormatter?.getRowNumberFormats(usedFields);

      if (pullMode === PullMode.APPEND && detectedRange && detectedHeaders) {
        const rangeToAppend = extendedRange || detectedRange;
        if (usedFields.length > detectedHeaders.length + Number(needToAddDateTimeColumn)) {
          throw new Error(
            `Column count in 'Append' mode mismatch.` +
              `Expected: <= ${usedFields.length}, observed: ${detectedHeaders.length}`,
          );
        }

        if (needToAddDateTimeColumn) {
          const tableRangeInfo = await this.workbookApi.getRangeAddressInfo(rangeToAppend);
          const dateTimeCellAddress = RangeAddress.fromString(tableRangeInfo.address).getCell(
            0,
            detectedHeaders.length,
          );
          const dateTimeCellRange = await this.workbookApi.getRangeByAddress(dateTimeCellAddress.addressWithSheet);
          await this.workbookApi.writeRangeValue(dateTimeCellRange, [
            [PULL_FLOWSTEP_ADDITIONAL_HEADERS.AppendDateTime],
          ]);
        }

        const appendedRowCount = await this.workbookApi.appendRangeData(
          rangeToAppend,
          this.toRowStream(providerStream, usedFields, isDateTimeColumnEnabled).drop(1),
          {
            insertBehaviour: InsertBehaviour.always,
            fillExtraColumns: true,
            rowNumberFormats,
          },
        );

        if (cancelToken?.isCanceled()) {
          const reason = cancelToken.reason || 'Execution cancelled';
          this.eventBus.notify(new ExecutorCancelled(reason));
          return { error: new CancelError(reason) };
        }

        const tableRange = this.workbookApi.addRows(rangeToAppend, appendedRowCount);

        if (createDataTable) {
          await this.workbookApi.createTable(tableRange, { resizeExistent: true });
        }

        if (useDropdownsForPicklists) {
          await this.applyPicklists(usedFields, tableRange);
        }

        this.eventBus.notify(
          new ExecutorActionFinished('Execution finished', { resultSuccessRange: tableRange, warnings: [] }),
        );

        return { warnings: [] };
      }
      const rowCount = await this.workbookApi.writeRangeData(
        targetRange,
        this.toRowStream(providerStream, usedFields, isDateTimeColumnEnabled),
        {
          insertBehaviour: InsertBehaviour.afterEnd,
          fillExtraColumns: true,
          rowNumberFormats,
        },
      );

      if (cancelToken?.isCanceled()) {
        const reason = cancelToken.reason || 'Execution cancelled';
        this.eventBus.notify(new ExecutorCancelled(reason));
        return { error: new CancelError(reason) };
      }

      await this.workbookApi.truncateRangeRows(targetRange, rowCount);

      const tableRange = await this.workbookApi.getRangeBySize(
        targetWorksheetId,
        topLeftCellAddress,
        rowCount,
        usedFields.length,
      );

      if (tableRange) {
        if (createDataTable) {
          await this.workbookApi.createTable(tableRange, { resizeExistent: true });
        }

        if (!detectedRange && !extendedRange) {
          this.workbookApi.autofitColumns(tableRange);
        }

        if (useDropdownsForPicklists) {
          await this.applyPicklists(usedFields, tableRange);
        }
      } else {
        throw new NoDataError('Could not detect the range of data after the Pull operation');
      }

      this.eventBus.notify(
        new ExecutorActionFinished('Execution finished', { resultSuccessRange: tableRange, warnings: [] }),
      );

      return { warnings: [] };
    } catch (error: unknown) {
      if (error instanceof NoDataError) {
        this.eventBus.notify(new ExecutorActionFinished('Execution finished with warnings', { warnings: [error] }));
        return { warnings: [error] };
      }
      if (error instanceof CancelError) {
        return { error };
      }
      this.eventBus.notify(new ExecutorActionError(error as Error));
      return { warnings: [], error };
    } finally {
      if (this.flowstep.options.timestampCell) {
        await this.setLastRunTimestamp();
      }
    }
  }

  private toRowStream(providerStream: Highland.Stream<any>, fields: DataField[], withDateTimeColumn: boolean) {
    let isHeaderRow = true;

    return providerStream
      .filter((item: any) => {
        if (isMetadata(item)) {
          this.processMetaItem(item);
          return false;
        }
        return true;
      })
      .map((rowValues) => {
        if (isHeaderRow) {
          isHeaderRow = false;
          const result = this.convertHeaderValues(rowValues, fields);
          withDateTimeColumn && result.push(PULL_FLOWSTEP_ADDITIONAL_HEADERS.AppendDateTime);
          return result;
        }
        const result = this.convertRowValues(rowValues, fields);
        withDateTimeColumn && result.push(this.timestamp);
        return result;
      })
      .tap(() => {
        this.recivedRowsCount++;
        if (this.recivedRowsCount % 1000 === 0) {
          this.eventBus.notify(new ExecutorActionEvent('Receiving data', `${this.recivedRowsCount} rows`));
        }
      });
  }

  private convertHeaderValues(headerValues: any, usedFields: DataField[]) {
    if (!Array.isArray(headerValues)) {
      return headerValues;
    }

    return headerValues.map((_, index) => usedFields[index].label);
  }

  private convertRowValues(rowValues: any[], fields: DataField[]) {
    if (!Array.isArray(rowValues)) {
      return rowValues;
    }

    return rowValues.map((value, index) =>
      this.valueConverter.toExcelValue(value, fields[index], { timezone: this.timezone }),
    );
  }

  private processMetaItem(item: MetadataResult): void {
    if (item.type === STREAM_METADATA.TIMESTAMP) {
      this.timestamp = item.payload.timestamp;
    }
  }

  private async setLastRunTimestamp() {
    if (!this.timestamp) {
      return;
    }
    const { worksheetId, address } = this.flowstep.options.timestampCell as CellAddressPickerValue;
    const range = await this.workbookApi.getRangeByAddress(`${worksheetId}!${address}`);
    if (!range) {
      return;
    }
    await this.workbookApi.writeRangeValue(range, [[this.timestamp]]);
  }

  private async applyPicklists(fileds: DataField[], tableRange: any) {
    const picklistsItems = fileds.map((field: DataField) =>
      field.options?.length ? field.options.map((option) => option.label) : [],
    );

    await this.workbookApi.applyPickLists(tableRange, this.flowstep.id, picklistsItems);
  }
}

export default PullExecutor;
